import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MessageCircle, RefreshCw, Link2, ShieldAlert, CheckCircle2, Smartphone } from 'lucide-react';
import { magSend } from '../../services/superuserApi';

/*
 * MagSend de UM ginásio, na ficha do portal.
 *
 * O que este cartão substitui: três campos onde se colavam à mão o projecto, o alias e o caminho
 * da chave. Ninguém os preenchia, e o resultado medido era todos os ginásios a sair pelo mesmo
 * número de WhatsApp, a gastar a mesma carteira de SMS e a aparecer no mesmo histórico.
 *
 * O cartão mostra o estado e resolve o que é resolúvel aqui:
 *  - «Ligar ao MagSend» cria o cliente, o projecto `gymag-<slug>` e a chave do ginásio;
 *  - «Ligar WhatsApp» pede a ligação e mostra o QR (o número novo é criado no primeiro pedido);
 *  - o estado é perguntado a cada 3 s, no máximo 2 minutos, e só enquanto o QR está à espera.
 *
 * Regras que o ecrã guarda: nenhuma chave aparece aqui (só o prefixo); um erro de configuração
 * tem uma frase e uma saída — nunca um spinner; alvos ≥ 44 px e texto ≥ 12 px, porque isto é
 * usado ao telemóvel.
 */

const TAP = 44;

// 3 s × 40 = 2 minutos: o tempo que o contrato dá a um QR para ser lido antes de expirar.
export const POLL_MS = 3000;
export const MAX_POLLS = 40;

const btn = (extra = {}) => ({
  minHeight: TAP,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid var(--border, #2b3440)',
  background: 'var(--bg-secondary, #161b22)',
  color: 'var(--text-primary, #e6edf3)',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  ...extra,
});

const primary = (extra = {}) => btn({
  background: 'var(--accent-primary, #2f81f7)',
  borderColor: 'var(--accent-primary, #2f81f7)',
  color: '#fff',
  ...extra,
});

const chip = (background, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background,
  color,
  whiteSpace: 'nowrap',
});

const TONES = {
  open: { bg: 'var(--success-bg, #12351f)', fg: 'var(--success-text, #7ee787)', icon: CheckCircle2 },
  'not-connected': { bg: 'var(--warning-bg, #3a2a10)', fg: 'var(--warning-text, #f0b72f)', icon: Smartphone },
  closed: { bg: 'var(--warning-bg, #3a2a10)', fg: 'var(--warning-text, #f0b72f)', icon: Smartphone },
  connecting: { bg: 'var(--warning-bg, #3a2a10)', fg: 'var(--warning-text, #f0b72f)', icon: Smartphone },
  'not-configured': { bg: 'var(--error-bg, #3d1518)', fg: 'var(--error-text, #ff7b72)', icon: ShieldAlert },
  unavailable: { bg: 'var(--bg-tertiary, #1c2129)', fg: 'var(--text-muted, #8b949e)', icon: MessageCircle },
};

export const toneFor = (state) => TONES[state] || TONES.unavailable;

const apiError = (e, fallback) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

const MagSendPanel = ({ instanceId, instanceName }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [connect, setConnect] = useState(null);       // { qrCode, state, label }
  const [pendingApply, setPendingApply] = useState(false);
  const [liveState, setLiveState] = useState(null);   // { state, label, connected, smsCredits }
  const [expired, setExpired] = useState(false);

  const pollsRef = useRef(0);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await magSend.card(instanceId);
      setCard(res.data);
      setError(null);
    } catch (e) {
      // Uma falha de rede diz que falhou e deixa repetir — nunca se mostra um cartão vazio como
      // se o ginásio não tivesse MagSend nenhum.
      const detail = e?.response?.data?.message || e?.response?.data?.error || e?.message;
      setError(detail
        ? `Não foi possível ler o estado do MagSend (${detail}).`
        : 'Não foi possível ler o estado do MagSend.');
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  // Polling do estado: só enquanto há um QR à espera. Pára ao ligar, ao expirar (2 min), ao sair
  // da página ou ao desmontar. Nunca em ciclo apertado.
  const stopPolling = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const poll = useCallback(async () => {
    try {
      const res = await magSend.state(instanceId);
      setLiveState(res.data);
      if (res.data?.connected) {
        stopPolling();
        setConnect(null);
        setExpired(false);
        toast.success('WhatsApp ligado a este ginásio.');
        load();
        return;
      }
    } catch (e) {
      // Um erro no polling não interrompe o ecrã: mostra-se o último estado conhecido e o
      // utilizador pode pedir outro QR.
    }

    pollsRef.current += 1;
    if (pollsRef.current >= MAX_POLLS) {
      stopPolling();
      setExpired(true);
      return;
    }
    timerRef.current = setTimeout(poll, POLL_MS);
  }, [instanceId, load, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollsRef.current = 0;
    setExpired(false);
    timerRef.current = setTimeout(poll, POLL_MS);
  }, [poll, stopPolling]);

  const doLink = async () => {
    setBusy('link');
    try {
      const res = await magSend.link(instanceId);
      setCard(res.data.card);
      setPendingApply(res.data.applied === false);
      toast.success(res.data.applied
        ? 'Ligado ao MagSend e aplicado ao ginásio.'
        : 'Ligado ao MagSend. Falta aplicar ao ginásio.');
    } catch (e) {
      toast.error(apiError(e, 'Não foi possível ligar este ginásio ao MagSend.'));
      load();
    } finally {
      setBusy(null);
    }
  };

  const doApply = async () => {
    setBusy('apply');
    try {
      const res = await magSend.apply(instanceId);
      setPendingApply(false);
      toast.success(res.data.message || 'Configuração aplicada.');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Não foi possível aplicar a configuração ao ginásio.'));
    } finally {
      setBusy(null);
    }
  };

  const doConnect = async () => {
    setBusy('connect');
    setExpired(false);
    try {
      const res = await magSend.connect(instanceId);
      setConnect(res.data);
      if (res.data.state === 'open') {
        toast.success('Este número já está ligado.');
        load();
      } else {
        startPolling();
      }
    } catch (e) {
      // 409 com needsSetup = falta configuração: uma frase clara, sem spinner e sem QR.
      const needsSetup = e?.response?.status === 409 && e?.response?.data?.needsSetup;
      setConnect(null);
      toast.error(apiError(e, needsSetup
        ? 'Integração MagSend por configurar neste ginásio.'
        : 'Não foi possível pedir o código de ligação.'));
    } finally {
      setBusy(null);
    }
  };

  const doRotate = async () => {
    if (!window.confirm('Rodar a chave deste ginásio? A anterior deixa de funcionar imediatamente.')) return;
    setBusy('rotate');
    try {
      const res = await magSend.rotate(instanceId);
      setCard(res.data.card);
      setPendingApply(res.data.applied === false);
      toast.success('Chave rodada e aplicada.');
    } catch (e) {
      toast.error(apiError(e, 'Não foi possível rodar a chave.'));
    } finally {
      setBusy(null);
    }
  };

  const doToggleActive = async () => {
    const active = card?.active === false;
    setBusy('active');
    try {
      const res = active ? await magSend.reactivate(instanceId) : await magSend.suspend(instanceId);
      toast.success(res.data.message || 'Estado do MagSend actualizado.');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Não foi possível alterar o estado no MagSend.'));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-muted, #8b949e)' }}>A ler o MagSend deste ginásio…</div>;
  }

  if (error && !card) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: 'var(--error-text, #ff7b72)' }}>{error}</span>
        <button type="button" style={btn()} onClick={load}>Tentar outra vez</button>
      </div>
    );
  }

  const state = liveState?.state || card?.whatsapp?.state || 'unavailable';
  const label = liveState?.label || card?.whatsapp?.label || 'Temporariamente indisponível';
  const tone = toneFor(state);
  const Icon = tone.icon;
  // Um QR expirado deixa de ser mostrado: um código que já não liga nada é pior do que nenhum.
  const qrCode = expired ? null : connect?.qrCode;
  const credits = card?.sms?.credits;
  const canConnect = card?.canConnect && state !== 'open';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted, #8b949e)', fontWeight: 600 }}>WhatsApp</span>
        <span style={chip(tone.bg, tone.fg)}>
          <Icon size={13} aria-hidden="true" />
          {label}
        </span>
        {typeof credits === 'number' && (
          <span style={chip('var(--bg-tertiary, #1c2129)', 'var(--text-secondary, #c9d1d9)')}>
            SMS: {credits} créditos
          </span>
        )}
        {card?.active === false && (
          <span style={chip('var(--error-bg, #3d1518)', 'var(--error-text, #ff7b72)')}>suspenso no MagSend</span>
        )}
      </div>

      {card?.project && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary, #c9d1d9)', wordBreak: 'break-all' }}>
          Projecto: <strong style={{ fontFamily: 'monospace' }}>{card.project}</strong>
          {card.keyPrefixes?.length > 0 && (
            <span style={{ color: 'var(--text-muted, #8b949e)' }}> · chave {card.keyPrefixes[0]}</span>
          )}
        </div>
      )}

      {card?.detail && (
        <div style={{ fontSize: 12, color: state === 'not-configured' ? 'var(--error-text, #ff7b72)' : 'var(--text-muted, #8b949e)' }}>
          {card.detail}
        </div>
      )}

      {pendingApply && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--warning-text, #f0b72f)' }}>
            Projecto e chave gravados, mas o ginásio ainda não os leu.
          </span>
          <button type="button" style={btn()} onClick={doApply} disabled={busy === 'apply'}>
            {busy === 'apply' ? 'A aplicar…' : 'Aplicar agora'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {!card?.linked && (
          <button type="button" style={primary()} onClick={doLink} disabled={busy === 'link'}
            aria-label={`Ligar ${instanceName} ao MagSend`}>
            <Link2 size={15} aria-hidden="true" />
            {busy === 'link' ? 'A ligar…' : 'Ligar ao MagSend'}
          </button>
        )}

        {card?.linked && canConnect && !qrCode && !expired && (
          <button type="button" style={primary()} onClick={doConnect} disabled={busy === 'connect'}
            aria-label={`Ligar WhatsApp de ${instanceName}`}>
            <MessageCircle size={15} aria-hidden="true" />
            {busy === 'connect' ? 'A pedir código…' : 'Ligar WhatsApp'}
          </button>
        )}

        {(qrCode || expired) && (
          <button type="button" style={primary()} onClick={doConnect} disabled={busy === 'connect'}>
            <RefreshCw size={15} aria-hidden="true" />
            {busy === 'connect' ? 'A pedir…' : expired ? 'Gerar outro código' : 'Atualizar código'}
          </button>
        )}
      </div>

      {qrCode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <img
            src={qrCode}
            alt="Código QR para ligar o WhatsApp deste ginásio"
            width={190}
            height={190}
            style={{ width: 190, height: 190, imageRendering: 'pixelated', background: '#fff', borderRadius: 8 }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #c9d1d9)', maxWidth: 320 }}>
            WhatsApp → Dispositivos ligados → Ligar dispositivo, e ler este código.
            O código expira em menos de um minuto; «Atualizar código» gera outro.
          </span>
        </div>
      )}

      {expired && (
        <span style={{ fontSize: 12, color: 'var(--warning-text, #f0b72f)' }}>
          O código expirou sem ser lido. Carregar em «Ligar WhatsApp» para gerar outro.
        </span>
      )}

      {card?.linked && (
        <details>
          <summary style={{ fontSize: 12, color: 'var(--text-muted, #8b949e)', cursor: 'pointer', minHeight: 32 }}>
            Avançado
          </summary>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            <button type="button" style={btn()} onClick={doRotate} disabled={busy === 'rotate'}>
              {busy === 'rotate' ? 'A rodar…' : 'Rodar chave'}
            </button>
            <button type="button" style={btn()} onClick={doToggleActive} disabled={busy === 'active'}>
              {card.active === false ? 'Reactivar no MagSend' : 'Suspender no MagSend'}
            </button>
          </div>
        </details>
      )}
    </div>
  );
};

export default MagSendPanel;
